import type { TargetPosition, FontSettings, WordInfo, WordOffset, LetterInfo, LetterOffset, TextAlign } from '@/types'
import { calculateIntelligentSpacing } from './persianTypography'
import { calculateWordPadding, getRecommendedExpansion } from './text'

export interface ScanConfig {
  text: string
  fontName: string
  fontSettings: FontSettings
  density: number
  canvasWidth: number
  canvasHeight: number
  wordOffsets?: WordOffset[]
  letterOffsets?: LetterOffset[]
  gridMode?: boolean  // When true, uses precise grid rendering without jitter
}

export interface ScanResult {
  positions: TargetPosition[]
  words: WordInfo[]
  letters: LetterInfo[]
}

interface ScanCanvas {
  width: number
  height: number
}

interface ScanCanvasContext {
  font: string
  textBaseline: CanvasTextBaseline
  direction: CanvasDirection
  textAlign: CanvasTextAlign
  fillStyle: string | CanvasGradient | CanvasPattern
  measureText: (text: string) => TextMetrics
  fillRect: (x: number, y: number, width: number, height: number) => void
  fillText: (text: string, x: number, y: number) => void
  getImageData: (sx: number, sy: number, sw: number, sh: number) => ImageData
  clearRect: (x: number, y: number, width: number, height: number) => void
}

interface LineBounds {
  lineIndex: number
  minX: number
  maxX: number
  minY: number
  maxY: number
  particleIndices: number[]
}

/**
 * Apply alignment and centering to particle positions
 *
 * Alignment Strategy:
 * 1. Calculate actual bounding box of all particles
 * 2. Center the entire text block on the canvas
 * 3. For right/left alignment, align each line's particles:
 *    - Right: align rightmost particles of each line to block's right edge
 *    - Left: align leftmost particles of each line to block's left edge
 *    - Center: center each line within the block
 */
function applyAlignmentAndCentering(
  positions: TargetPosition[],
  words: WordInfo[],
  letters: LetterInfo[],
  canvasWidth: number,
  canvasHeight: number,
  textAlign: TextAlign
): void {
  if (positions.length === 0) return

  // Group positions by line (using wordIndex to find line)
  const wordToLine = new Map<number, number>()
  for (const word of words) {
    wordToLine.set(word.wordIndex, word.lineIndex)
  }

  // Calculate bounds for each line and overall bounds
  const lineBoundsMap = new Map<number, LineBounds>()
  let overallMinX = Infinity
  let overallMaxX = -Infinity
  let overallMinY = Infinity
  let overallMaxY = -Infinity

  for (let i = 0; i < positions.length; i++) {
    const pos = positions[i]
    const lineIndex = pos.wordIndex !== undefined ? (wordToLine.get(pos.wordIndex) ?? 0) : 0

    // Update overall bounds
    overallMinX = Math.min(overallMinX, pos.x)
    overallMaxX = Math.max(overallMaxX, pos.x)
    overallMinY = Math.min(overallMinY, pos.y)
    overallMaxY = Math.max(overallMaxY, pos.y)

    // Update line bounds
    let lineBounds = lineBoundsMap.get(lineIndex)
    if (!lineBounds) {
      lineBounds = {
        lineIndex,
        minX: Infinity,
        maxX: -Infinity,
        minY: Infinity,
        maxY: -Infinity,
        particleIndices: [],
      }
      lineBoundsMap.set(lineIndex, lineBounds)
    }
    lineBounds.minX = Math.min(lineBounds.minX, pos.x)
    lineBounds.maxX = Math.max(lineBounds.maxX, pos.x)
    lineBounds.minY = Math.min(lineBounds.minY, pos.y)
    lineBounds.maxY = Math.max(lineBounds.maxY, pos.y)
    lineBounds.particleIndices.push(i)
  }

  // Calculate text block center and canvas center for centering offset
  const textCenterX = (overallMinX + overallMaxX) / 2
  const textCenterY = (overallMinY + overallMaxY) / 2
  const canvasCenterX = canvasWidth / 2
  const canvasCenterY = canvasHeight / 2

  // First, center the entire text block
  const centerOffsetX = canvasCenterX - textCenterX
  const centerOffsetY = canvasCenterY - textCenterY

  // Apply centering offset to all positions
  for (const pos of positions) {
    pos.x += centerOffsetX
    pos.y += centerOffsetY
  }

  // Update line bounds after centering
  for (const lineBounds of lineBoundsMap.values()) {
    lineBounds.minX += centerOffsetX
    lineBounds.maxX += centerOffsetX
    lineBounds.minY += centerOffsetY
    lineBounds.maxY += centerOffsetY
  }

  // Calculate new overall bounds after centering
  const newOverallMinX = overallMinX + centerOffsetX
  const newOverallMaxX = overallMaxX + centerOffsetX

  // Now apply alignment within the centered block
  // The block's right edge is the rightmost particle, left edge is leftmost
  const blockRightEdge = newOverallMaxX
  const blockLeftEdge = newOverallMinX

  if (textAlign === 'right') {
    // Right align: each line's rightmost particle should align to block's right edge
    for (const lineBounds of lineBoundsMap.values()) {
      const lineRightEdge = lineBounds.maxX
      const alignOffset = blockRightEdge - lineRightEdge

      // Apply offset to all particles in this line
      for (const idx of lineBounds.particleIndices) {
        positions[idx].x += alignOffset
      }
    }
  } else if (textAlign === 'left') {
    // Left align: each line's leftmost particle should align to block's left edge
    for (const lineBounds of lineBoundsMap.values()) {
      const lineLeftEdge = lineBounds.minX
      const alignOffset = blockLeftEdge - lineLeftEdge

      // Apply offset to all particles in this line
      for (const idx of lineBounds.particleIndices) {
        positions[idx].x += alignOffset
      }
    }
  }
  // Center alignment: already centered, no additional offset needed

  // Update word positions to match particle positions
  for (const word of words) {
    // Apply centering offset
    word.baseX += centerOffsetX
    word.baseY += centerOffsetY

    // Apply alignment offset if needed
    if (textAlign === 'right' || textAlign === 'left') {
      const lineBounds = lineBoundsMap.get(word.lineIndex)
      if (lineBounds) {
        if (textAlign === 'right') {
          const alignOffset = blockRightEdge - lineBounds.maxX
          word.baseX += alignOffset
        } else {
          const alignOffset = blockLeftEdge - lineBounds.minX
          word.baseX += alignOffset
        }
      }
    }
  }

  // PERF: Create word lookup map for O(1) access instead of O(n) find()
  const wordByIndex = new Map<number, WordInfo>()
  for (const word of words) {
    wordByIndex.set(word.wordIndex, word)
  }

  // Update letter positions to match particle positions
  for (const letter of letters) {
    // Apply centering offset
    letter.baseX += centerOffsetX
    letter.baseY += centerOffsetY

    // Find the word this letter belongs to for line info (O(1) lookup)
    const word = wordByIndex.get(letter.wordIndex)
    if (word) {
      // Apply alignment offset if needed
      if (textAlign === 'right' || textAlign === 'left') {
        const lineBounds = lineBoundsMap.get(word.lineIndex)
        if (lineBounds) {
          if (textAlign === 'right') {
            const alignOffset = blockRightEdge - lineBounds.maxX
            letter.baseX += alignOffset
          } else {
            const alignOffset = blockLeftEdge - lineBounds.minX
            letter.baseX += alignOffset
          }
        }
      }
    }
  }
}

/**
 * Scan text pixels to extract target positions for particles
 * Uses Canvas 2D API for proper RTL Persian text rendering
 *
 * Text is always centered on the canvas, with alignment applied
 * per-line relative to the text block bounds.
 */
export function scanTextPixelsWithCanvas(config: ScanConfig, canvas: ScanCanvas, ctx: ScanCanvasContext): ScanResult {
  const { text, fontName, fontSettings, density, canvasWidth, canvasHeight, wordOffsets = [], letterOffsets = [], gridMode = false } = config
  const { fontSize, lineHeight, wordSpacing, textAlign } = fontSettings
  canvas.width = canvasWidth
  canvas.height = canvasHeight

  // Calculate dynamic font size based on canvas, text length, and number of lines
  const lines = text.split('\n')
  const maxLineLength = Math.max(...lines.map((line) => line.length), 1)
  const numLines = lines.filter((l) => l.trim().length > 0).length || 1

  // Scale font based on longest line and number of lines
  const fontSizeByWidth = canvasWidth / (maxLineLength * 0.8)
  const fontSizeByHeight = canvasHeight / (numLines * 1.5)
  let calculatedFontSize = Math.min(fontSizeByWidth, fontSizeByHeight, canvasHeight * 0.35)

  // Use user-defined fontSize as a scale factor
  calculatedFontSize = Math.max(40, Math.min(300, (calculatedFontSize * fontSize) / 120))

  // Calculate line heights
  const lineHeightPx = calculatedFontSize * lineHeight

  // Collect all words with their positions
  const allWords: WordInfo[] = []
  const wordBounds: Array<{ wordIndex: number; lineIndex: number; lineY: number; minX: number; maxX: number; minY: number; maxY: number }> = []
  let globalWordIndex = 0

  // Collect all letters with their positions
  const allLetters: LetterInfo[] = []
  const letterBounds: Array<{ letterIndex: number; minX: number; maxX: number; minY: number; maxY: number }> = []
  let globalLetterIndex = 0

  // Set up text rendering for measurements
  // Always use 'alphabetic' baseline for consistent measurement and positioning
  ctx.font = `${calculatedFontSize}px "${fontName}"`
  ctx.textBaseline = 'alphabetic'
  ctx.direction = 'rtl'

  // First, measure all lines to find the widest one AND measure max ascent/descent
  // This is critical for Nastaliq where sarkesh strokes can extend 2-3x font size above baseline
  const lineWidths: number[] = []
  const lineWordData: Array<ReturnType<typeof calculateIntelligentSpacing>> = []
  let maxAscent = 0
  let maxDescent = 0

  for (let lineIndex = 0; lineIndex < lines.length; lineIndex++) {
    const lineText = lines[lineIndex].trim()
    if (lineText.length === 0) {
      lineWidths.push(0)
      lineWordData.push({ measurements: [], spacings: [], totalWidth: 0 })
      continue
    }
    const words = lineText.split(' ').filter((w) => w.length > 0)
    if (words.length === 0) {
      lineWidths.push(0)
      lineWordData.push({ measurements: [], spacings: [], totalWidth: 0 })
      continue
    }
    const wordData = calculateIntelligentSpacing(words, ctx, calculatedFontSize, wordSpacing)
    lineWidths.push(wordData.totalWidth)
    lineWordData.push(wordData)

    // Measure actual glyph bounds for each word to find max ascent/descent
    // Use NastaliqMetrics for character-specific padding
    for (const word of words) {
      const metrics = ctx.measureText(word)
      const rawAscent = metrics.actualBoundingBoxAscent ?? calculatedFontSize * 0.8
      const rawDescent = metrics.actualBoundingBoxDescent ?? calculatedFontSize * 0.4

      // Use character-aware padding from NastaliqMetrics
      const charPadding = calculateWordPadding(word, calculatedFontSize)
      const paddedAscent = rawAscent + charPadding.top + calculatedFontSize * 0.3
      const paddedDescent = rawDescent + charPadding.bottom + calculatedFontSize * 0.2

      maxAscent = Math.max(maxAscent, paddedAscent)
      maxDescent = Math.max(maxDescent, paddedDescent)
    }
  }

  // Ensure we have minimum values
  maxAscent = Math.max(maxAscent, calculatedFontSize * 1.5)
  maxDescent = Math.max(maxDescent, calculatedFontSize * 0.5)

  // CRITICAL FIX: Calculate startY based on actual max ascent/descent
  // This ensures sarkesh strokes are never clipped at canvas boundaries
  // Total text height = first line ascent + (numLines-1) * lineHeight + last line descent
  const actualTotalHeight = maxAscent + (numLines - 1) * lineHeightPx + maxDescent

  // Center the text block vertically, ensuring it fits within canvas
  // startY is the baseline of the first line
  const verticalPadding = Math.max(0, (canvasHeight - actualTotalHeight) / 2)
  const startYAdjusted = verticalPadding + maxAscent

  // For initial rendering, we render text centered
  // Alignment will be applied after pixel scanning based on actual particle positions
  const canvasCenterX = canvasWidth / 2

  // First pass: calculate word positions (render centered for now)
  for (let lineIndex = 0; lineIndex < lines.length; lineIndex++) {
    const lineText = lines[lineIndex].trim()
    if (lineText.length === 0) continue

    const lineY = startYAdjusted + lineIndex * lineHeightPx

    const words = lineText.split(' ').filter((w) => w.length > 0)
    if (words.length === 0) continue

    const wordData = lineWordData[lineIndex]
    const lineWidth = lineWidths[lineIndex]

    // Render centered - alignment will be applied post-scan
    let xPos = canvasCenterX + lineWidth / 2

    for (let i = 0; i < words.length; i++) {
      // Bounds check for array access safety
      if (i >= wordData.measurements.length || i >= wordData.spacings.length) {
        console.warn(`[textScanner] Array bounds exceeded at index ${i}`)
        continue
      }

      const wordWidth = wordData.measurements[i].width

      // Measure actual glyph bounds for this specific word
      // Nastaliq sarkesh strokes can extend 2-3x font size above baseline
      const wordMetrics = ctx.measureText(words[i])

      // Get actual ascent/descent from font metrics
      // Use character-aware padding from NastaliqMetrics
      const rawAscent = wordMetrics.actualBoundingBoxAscent ?? calculatedFontSize * 0.8
      const rawDescent = wordMetrics.actualBoundingBoxDescent ?? calculatedFontSize * 0.4

      // Apply character-specific padding for Nastaliq calligraphy
      const charPadding = calculateWordPadding(words[i], calculatedFontSize)
      const ascentPadding = charPadding.top + calculatedFontSize * 0.3
      const descentPadding = charPadding.bottom + calculatedFontSize * 0.2

      const actualAscent = rawAscent + ascentPadding
      const actualDescent = rawDescent + descentPadding
      const actualHeight = actualAscent + actualDescent

      // Get offset for this word (used for pixel scanning, not stored in wordInfo)
      const offset = wordOffsets[globalWordIndex] || { x: 0, y: 0 }

      // Word position - baseX is center, baseY is the baseline position
      const wordCenterX = xPos - wordWidth / 2
      const wordBaselineY = lineY  // This is where the alphabetic baseline sits

      allWords.push({
        text: words[i],
        lineIndex,
        wordIndex: globalWordIndex,
        baseX: wordCenterX,      // Center X position
        baseY: wordBaselineY,    // Baseline Y position
        width: wordWidth,
        height: actualHeight,    // Actual visual height including sarkesh
        fontSize: calculatedFontSize,
        ascent: actualAscent,    // Height above baseline
        descent: actualDescent,  // Height below baseline
      })

      // Store bounds for this word (WITH offset for pixel scanning)
      // Use actual ascent/descent for proper bounds
      wordBounds.push({
        wordIndex: globalWordIndex,
        lineIndex,
        lineY: lineY + offset.y,  // Store actual line Y for proximity comparison
        minX: xPos - wordWidth + offset.x,
        maxX: xPos + offset.x,
        minY: lineY - actualAscent + offset.y,
        maxY: lineY + actualDescent + offset.y,
      })

      // Measure and store individual letters within this word
      // For RTL text, letters are positioned from right to left
      const wordText = words[i]
      let letterXPos = xPos // Start from right edge of word (RTL)

      // FIXED: Persian ligatures make individual letter measurement unreliable.
      // Letters change shape based on position (isolated, initial, medial, final).
      // Instead, distribute word width evenly across all letters.
      // This gives more accurate positioning for particle distribution.
      const avgLetterWidth = wordText.length > 0 ? wordWidth / wordText.length : 0

      for (let letterIdx = 0; letterIdx < wordText.length; letterIdx++) {
        const adjustedLetterWidth = avgLetterWidth

        // Get offset for this letter
        const letterOffset = letterOffsets[globalLetterIndex] || { x: 0, y: 0 }

        // Letter center position (RTL: moving left from xPos)
        const letterCenterX = letterXPos - adjustedLetterWidth / 2
        const letterCenterY = lineY

        allLetters.push({
          letter: wordText[letterIdx],
          wordIndex: globalWordIndex,
          letterIndexInWord: letterIdx,
          globalIndex: globalLetterIndex,
          baseX: letterCenterX,  // Original position without offset
          baseY: letterCenterY,  // Baseline Y position
          width: adjustedLetterWidth,
          height: actualHeight,  // Use actual height including sarkesh
          wordText: wordText,    // Store full word for contextual form calculation
          wordLength: wordText.length,
        })

        // Store bounds for this letter (WITH offset for pixel scanning)
        // Use actual ascent/descent for proper bounds
        letterBounds.push({
          letterIndex: globalLetterIndex,
          minX: letterXPos - adjustedLetterWidth + letterOffset.x,
          maxX: letterXPos + letterOffset.x,
          minY: lineY - actualAscent + letterOffset.y,
          maxY: lineY + actualDescent + letterOffset.y,
        })

        letterXPos -= adjustedLetterWidth
        globalLetterIndex++
      }

      xPos -= wordWidth + wordData.spacings[i]
      globalWordIndex++
    }
  }

  // Second pass: render each word separately with its offset
  // Use 'alphabetic' baseline to match our positioning calculations
  ctx.fillStyle = 'black'
  ctx.fillRect(0, 0, canvasWidth, canvasHeight)
  ctx.fillStyle = 'white'
  ctx.font = `${calculatedFontSize}px "${fontName}"`
  ctx.textBaseline = 'alphabetic'
  ctx.direction = 'rtl'
  ctx.textAlign = 'right'

  globalWordIndex = 0
  for (let lineIndex = 0; lineIndex < lines.length; lineIndex++) {
    const lineText = lines[lineIndex].trim()
    if (lineText.length === 0) continue

    // Use startYAdjusted which accounts for sarkesh height
    const lineY = startYAdjusted + lineIndex * lineHeightPx
    const words = lineText.split(' ').filter((w) => w.length > 0)
    if (words.length === 0) continue

    const wordData = lineWordData[lineIndex]
    const lineWidth = lineWidths[lineIndex]

    // Render centered
    let xPos = canvasCenterX + lineWidth / 2

    for (let i = 0; i < words.length; i++) {
      // Bounds check for array access safety
      if (i >= wordData.measurements.length || i >= wordData.spacings.length) continue

      const offset = wordOffsets[globalWordIndex] || { x: 0, y: 0 }
      ctx.fillText(words[i], xPos + offset.x, lineY + offset.y)
      xPos -= wordData.measurements[i].width + wordData.spacings[i]
      globalWordIndex++
    }
  }

  // Get image data for pixel scanning
  const imageData = ctx.getImageData(0, 0, canvasWidth, canvasHeight)
  const pixels = imageData.data

  // Scan pixels with density step to find text positions
  const step = Math.max(1, density)
  const moduleSize = density * 0.8
  const targetPositions: TargetPosition[] = []

  for (let y = 0; y < canvasHeight; y += step) {
    for (let x = 0; x < canvasWidth; x += step) {
      const index = (x + y * canvasWidth) * 4
      const brightness = pixels[index]

      // Grid mode uses higher threshold (180) for precise grid-like rendering
      // Smooth mode uses threshold (128) to capture fine details and diacritical marks
      // Persian calligraphy has delicate strokes that require lower threshold
      const threshold = gridMode ? 180 : 128

      if (brightness > threshold) {
        // Find which word this pixel belongs to
        // Use expanded bounds to include diacritical marks (dots) and sarkesh strokes
        // that may extend far above/below the main letter body in Persian/Nastaliq calligraphy
        let pixelWordIndex: number | undefined
        let closestLineDistance = Infinity

        // Use getRecommendedExpansion for character-aware bounds expansion
        // This provides better coverage for tall sarkesh letters
        for (const bounds of wordBounds) {
          // Get word text to calculate character-specific expansion
          const wordInfo = allWords.find(w => w.wordIndex === bounds.wordIndex)
          const wordText = wordInfo?.text ?? ''
          const expansion = getRecommendedExpansion(wordText, calculatedFontSize)

          // Expand bounds to capture all calligraphic strokes
          const expandedMinX = bounds.minX - expansion.horizontal
          const expandedMaxX = bounds.maxX + expansion.horizontal
          const expandedMinY = bounds.minY - expansion.verticalAbove
          const expandedMaxY = bounds.maxY + expansion.verticalBelow
          if (x >= expandedMinX && x <= expandedMaxX && y >= expandedMinY && y <= expandedMaxY) {
            // MULTILINE FIX: When expanded bounds overlap between lines, prefer the closest line
            // This prevents pixels from line 2 being incorrectly assigned to line 1
            const distanceToLine = Math.abs(y - bounds.lineY)
            if (distanceToLine < closestLineDistance) {
              closestLineDistance = distanceToLine
              pixelWordIndex = bounds.wordIndex
            }
          }
        }

        // If pixel is not within any expanded word bounds, skip it
        // This prevents true artifact particles while keeping valid calligraphic strokes
        if (pixelWordIndex === undefined) {
          continue
        }

        // Apply random jitter only in smooth mode (not grid mode)
        // Jitter adds natural variation but can place particles slightly outside bounds
        const offsetX = gridMode ? 0 : (Math.random() - 0.5) * 2
        const offsetY = gridMode ? 0 : (Math.random() - 0.5) * 2

        targetPositions.push({
          x: x + offsetX,
          y: y + offsetY,
          size: moduleSize + (Math.random() - 0.5),
          wordIndex: pixelWordIndex,
        })
      }
    }
  }

  // Apply alignment and centering based on actual particle positions
  applyAlignmentAndCentering(targetPositions, allWords, allLetters, canvasWidth, canvasHeight, textAlign)

  // Clean up off-screen canvas to prevent memory leak
  // Setting dimensions to 0 releases GPU/canvas resources
  ctx.clearRect(0, 0, canvasWidth, canvasHeight)
  canvas.width = 0
  canvas.height = 0

  return { positions: targetPositions, words: allWords, letters: allLetters }
}

export function scanTextPixels(config: ScanConfig): ScanResult {
  const canvas = document.createElement('canvas')
  const ctx = canvas.getContext('2d')

  if (!ctx) {
    throw new Error('Canvas 2D context is not available.')
  }

  return scanTextPixelsWithCanvas(config, canvas, ctx as ScanCanvasContext)
}
